import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { RotateCcw, Search, Plus, Trash2 } from 'lucide-react';
import { useAutosave, clearDraft } from '../hooks/useAutosave';
import { useServerDrafts } from '../hooks/useServerDrafts';
import { DraftsPanel } from '../components/DraftsPanel';

const fmtCurrency = (v) => `$${Number(v || 0).toFixed(2)}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '';

const SR_KEY = 'webb_sales_return_draft';

const SalesReturns = () => {
  const navigate = useNavigate();
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);

  // R13: Form state starts BLANK on every visit. Server drafts are loaded into the DraftsPanel only.
  const _saved = null;
  const [soNumber, setSoNumber] = useState('');
  const [soData, setSoData] = useState(null);
  const [lookupError, setLookupError] = useState('');
  const [returnDate, setReturnDate] = useState(new Date().toISOString().split('T')[0]);
  const [returnItems, setReturnItems] = useState([]);
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);
  // R11: SO# typeahead — fetches matching orders as the user types
  const [soSuggestions, setSoSuggestions] = useState([]);
  const [showSoSuggestions, setShowSoSuggestions] = useState(false);

  // R13: Server-side drafts
  const { drafts: serverDrafts, currentDraftId, saveDraft, deleteDraft, clearCurrentDraft, setCurrentDraftId } =
    useServerDrafts('sales_return');

  // (Local autosave kept as a UI-speed cache, server is source of truth)
  useAutosave(SR_KEY, { soNumber, soData, returnDate, returnItems, remarks });

  // Server-side debounced save on every change
  useEffect(() => {
    if (!soNumber && returnItems.length === 0 && !remarks) return;
    const label = `${soData?.customer_name || soNumber || 'Return'} • ${returnItems.length} item${returnItems.length === 1 ? '' : 's'}`;
    saveDraft({ data: { soNumber, soData, returnDate, returnItems, remarks }, label });
  }, [soNumber, soData, returnDate, returnItems, remarks, saveDraft]);

  const handleResumeDraft = useCallback((draft) => {
    if (!draft || !draft.data) return;
    const d = draft.data;
    setSoNumber(d.soNumber || '');
    setSoData(d.soData || null);
    setReturnDate(d.returnDate || new Date().toISOString().split('T')[0]);
    setReturnItems(d.returnItems || []);
    setRemarks(d.remarks || '');
    setCurrentDraftId(draft.id);
    setShowForm(true);
  }, [setCurrentDraftId]);

  // Debounced search for SO# typeahead
  useEffect(() => {
    const q = soNumber.trim();
    if (!q || soData) {
      setSoSuggestions([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const orders = await api.request(`/api/sales-orders?search=${encodeURIComponent(q)}&limit=8`);
        // Only show valid SO matches (not already-fully-returned ones)
        setSoSuggestions((orders || []).filter(o => o.order_number && o.return_status !== 'fully_returned').slice(0, 8));
      } catch {
        setSoSuggestions([]);
      }
    }, 150);
    return () => clearTimeout(t);
  }, [soNumber, soData]);

  const fetchReturns = async () => {
    try {
      const data = await api.request('/api/sales-returns');
      setReturns(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchReturns(); }, []);

  const lookupSO = async (override) => {
    const num = (override || soNumber).trim();
    if (!num) return;
    setLookupError('');
    setSoData(null);
    setReturnItems([]);
    try {
      const orders = await api.request(`/api/sales-orders?search=${encodeURIComponent(num)}`);
      const match = orders.find(o => o.order_number === num);
      if (!match) {
        setLookupError(`Sales order ${num} not found`);
        return;
      }
      setSoData(match);
      // Pre-fill return items from SO line items with qty=0
      setReturnItems((match.line_items || []).map(li => ({
        variant_id: li.variant_id || '',
        variant_name: li.variant_name || '',
        sku: li.sku || '',
        unit_price: li.unit_price || 0,
        is_taxable: li.is_taxable !== false,
        max_qty: li.quantity || 0,
        quantity: 0,
        checked: false,
      })));
    } catch (_err) {
      setLookupError('Failed to look up sales order');
    }
  };

  const toggleReturnItem = (idx) => {
    setReturnItems(prev => prev.map((item, i) =>
      i === idx ? { ...item, checked: !item.checked, quantity: !item.checked ? item.max_qty : 0 } : item
    ));
  };

  const updateReturnQty = (idx, qty) => {
    const parsed = Math.min(Math.max(0, parseInt(qty) || 0), returnItems[idx].max_qty);
    setReturnItems(prev => prev.map((item, i) =>
      i === idx ? { ...item, quantity: parsed, checked: parsed > 0 } : item
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const items = returnItems.filter(i => i.quantity > 0);
    if (!soNumber.trim()) { alert('Enter a Sales Order #'); return; }
    if (items.length === 0) { alert('Set quantity > 0 for at least one item to return'); return; }

    setSaving(true);
    try {
      await api.request('/api/sales-returns', {
        method: 'POST',
        body: JSON.stringify({
          sales_order_number: soNumber.trim(),
          return_date: returnDate,
          remarks,
          line_items: items.map(i => ({
            variant_id: i.variant_id,
            variant_name: i.variant_name,
            sku: i.sku,
            quantity: i.quantity,
            unit_price: i.unit_price,
            is_taxable: i.is_taxable,
          })),
        }),
      });
      clearDraft(SR_KEY);
      // R13: delete the server draft now that the return is committed
      if (currentDraftId) { try { await deleteDraft(currentDraftId); } catch { /* non-fatal */ } }
      clearCurrentDraft();
      setShowForm(false);
      setSoNumber(''); setSoData(null); setReturnItems([]); setRemarks('');
      setReturnDate(new Date().toISOString().split('T')[0]);
      fetchReturns();
    } catch (err) {
      alert('Failed: ' + err.message);
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this return? Inventory adjustments will be reversed.')) return;
    try {
      await api.request(`/api/sales-returns/${id}`, { method: 'DELETE' });
      fetchReturns();
    } catch (err) { alert('Failed: ' + err.message); }
  };

  const filtered = returns.filter(r =>
    !search || r.return_number?.toLowerCase().includes(search.toLowerCase()) ||
    r.sales_order_number?.toLowerCase().includes(search.toLowerCase()) ||
    r.customer_name?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="sales-returns-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Sales Order Returns (Credit)</h1>
          <p className="text-sm text-muted-foreground mt-1">Process returns — items are credited back to inventory, deducted from statements and tax</p>
        </div>
        <button onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 text-sm font-medium"
          data-testid="new-return-btn">
          <Plus className="h-4 w-4" /> New Return
        </button>
      </div>

      {/* R13: Saved server-side drafts panel */}
      <DraftsPanel
        drafts={serverDrafts}
        onResume={handleResumeDraft}
        onDelete={deleteDraft}
        currentDraftId={currentDraftId}
        title="Saved Return Drafts"
      />

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-card border rounded-lg p-6 space-y-4" data-testid="return-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Sales Order # *</label>
              <div className="flex gap-2 relative">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={soNumber}
                    onChange={(e) => { setSoNumber(e.target.value); setSoData(null); setShowSoSuggestions(true); }}
                    onFocus={() => setShowSoSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSoSuggestions(false), 200)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); lookupSO(); } }}
                    placeholder="Start typing SO #..."
                    className="w-full px-3 py-2 border border-input rounded-md text-sm"
                    autoComplete="off"
                    data-testid="return-so-number"
                  />
                  {showSoSuggestions && soSuggestions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-md shadow-lg z-50 max-h-64 overflow-y-auto" data-testid="so-suggestions">
                      {soSuggestions.map((o) => (
                        <button
                          key={o.id}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setSoNumber(o.order_number);
                            setShowSoSuggestions(false);
                            // Pre-load the SO immediately
                            setTimeout(() => lookupSO(o.order_number), 0);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-muted text-sm border-b border-border last:border-b-0"
                          data-testid={`so-suggestion-${o.order_number}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div>
                              <div className="font-mono font-semibold">{o.order_number}</div>
                              <div className="text-xs text-muted-foreground">{o.customer_name}</div>
                            </div>
                            <div className="text-xs text-muted-foreground whitespace-nowrap">
                              {o.order_date ? new Date(o.order_date).toLocaleDateString() : ''}
                              {o.return_status === 'partially_returned' && (
                                <span className="ml-2 text-red-600 font-medium">Partial</span>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button type="button" onClick={() => lookupSO()}
                  className="px-3 py-2 bg-secondary text-secondary-foreground rounded-md text-sm hover:bg-secondary/80"
                  data-testid="lookup-so-btn">
                  <Search className="h-4 w-4" />
                </button>
              </div>
              {lookupError && <p className="text-red-500 text-xs mt-1">{lookupError}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Return Date</label>
              <input type="date" value={returnDate} onChange={(e) => setReturnDate(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="return-date" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Remarks</label>
              <input type="text" value={remarks} onChange={(e) => setRemarks(e.target.value)}
                placeholder="Reason for return" className="w-full px-3 py-2 border border-input rounded-md text-sm"
                data-testid="return-remarks" />
            </div>
          </div>

          {soData && (
            <div className="mt-2">
              <p className="text-sm text-muted-foreground mb-2">
                Customer: <span className="font-medium text-foreground">{soData.customer_name}</span> | 
                Total: <span className="font-medium text-foreground">{fmtCurrency(soData.total)}</span>
              </p>
              <table className="w-full text-sm border">
                <thead>
                  <tr className="bg-muted/30 border-b">
                    <th className="text-center px-3 py-2 w-10">Return</th>
                    <th className="text-left px-3 py-2">Item</th>
                    <th className="text-left px-3 py-2">SKU</th>
                    <th className="text-right px-3 py-2">Price</th>
                    <th className="text-center px-3 py-2">Ordered</th>
                    <th className="text-center px-3 py-2">Return Qty</th>
                    <th className="text-center px-3 py-2">Taxable</th>
                  </tr>
                </thead>
                <tbody>
                  {returnItems.map((item, idx) => (
                    <tr key={idx} className={`border-b ${item.checked ? 'bg-red-50/50' : ''}`}>
                      <td className="px-3 py-2 text-center">
                        <input type="checkbox" checked={!!item.checked}
                          onChange={() => toggleReturnItem(idx)}
                          className="h-4 w-4 rounded border-input accent-red-600"
                          data-testid={`return-check-${idx}`} />
                      </td>
                      <td className="px-3 py-2">{item.variant_name}</td>
                      <td className="px-3 py-2 text-muted-foreground">{item.sku}</td>
                      <td className="px-3 py-2 text-right">{fmtCurrency(item.unit_price)}</td>
                      <td className="px-3 py-2 text-center">{item.max_qty}</td>
                      <td className="px-3 py-2 text-center">
                        <input type="number" min="0" max={item.max_qty}
                          value={item.quantity} onChange={(e) => updateReturnQty(idx, e.target.value)}
                          className="w-20 px-2 py-1 border border-input rounded text-center text-sm disabled:opacity-40"
                          disabled={!item.checked}
                          data-testid={`return-qty-${idx}`} />
                      </td>
                      <td className="px-3 py-2 text-center">{item.is_taxable ? 'Yes' : 'No'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={saving || !soData}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-medium disabled:opacity-50"
              data-testid="submit-return-btn">
              {saving ? 'Processing...' : 'Process Return'}
            </button>
            <button type="button" onClick={async () => {
                setShowForm(false);
                clearDraft(SR_KEY);
                if (currentDraftId) { try { await deleteDraft(currentDraftId); } catch { /* non-fatal */ } }
                clearCurrentDraft();
                setSoNumber(''); setSoData(null); setReturnItems([]); setRemarks('');
              }}
              className="px-4 py-2 border rounded-md hover:bg-muted text-sm" data-testid="cancel-return-btn">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input type="text" placeholder="Search returns..." value={search} onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-input rounded-lg text-sm" data-testid="search-returns" />
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <RotateCcw className="h-12 w-12 mx-auto mb-3 opacity-40" />
          <p className="text-lg font-medium">No returns found</p>
        </div>
      ) : (
        <div className="bg-card border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="text-left px-4 py-3 font-medium">Return #</th>
                <th className="text-left px-4 py-3 font-medium">SO #</th>
                <th className="text-left px-4 py-3 font-medium">Date</th>
                <th className="text-left px-4 py-3 font-medium">Customer</th>
                <th className="text-right px-4 py-3 font-medium">Credit</th>
                <th className="text-right px-4 py-3 font-medium">Tax</th>
                <th className="text-right px-4 py-3 font-medium">Total</th>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} className="border-b hover:bg-muted/20 cursor-pointer" onClick={() => navigate(`/sales-returns/${r.id}`)}>
                  <td className="px-4 py-3 font-medium text-primary">{r.return_number}</td>
                  <td className="px-4 py-3">{r.sales_order_number}</td>
                  <td className="px-4 py-3 text-muted-foreground">{fmtDate(r.return_date)}</td>
                  <td className="px-4 py-3">{r.customer_name}</td>
                  <td className="px-4 py-3 text-right text-red-600">-{fmtCurrency(r.subtotal)}</td>
                  <td className="px-4 py-3 text-right text-red-600">-{fmtCurrency(r.tax)}</td>
                  <td className="px-4 py-3 text-right font-medium text-red-600">-{fmtCurrency(r.total)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(r.id); }}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded-md" data-testid={`delete-return-${r.return_number}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SalesReturns;
